// Pure helpers behind the chart's Overview tab and its one-line summary. No data is invented: every value is a field of a
// row the chart already fetched (`usePatientDocuments`, `usePatientMedications`, `usePatientConditions`, `usePatientAllergies`).
import type { PatientDocument } from '@/types/patient-chart-api';

/** Where a document comes from: the facility, else the doctor. A row naming neither has no provider (it is not counted as one). */
export function documentProvider(doc: Pick<PatientDocument, 'facilityName' | 'doctorName'>): string | null {
  return doc.facilityName?.trim() || doc.doctorName?.trim() || null;
}

export interface ProviderCount {
  name: string;
  count: number;
}

/** Providers with the number of documents each gave, the largest first (ties in name order). */
export function providerCounts(docs: readonly Pick<PatientDocument, 'facilityName' | 'doctorName'>[]): ProviderCount[] {
  const counts = new Map<string, number>();
  for (const d of docs) {
    const name = documentProvider(d);
    if (name) counts.set(name, (counts.get(name) ?? 0) + 1);
  }
  return [...counts]
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
}

function when(doc: Pick<PatientDocument, 'documentDate' | 'createdOn'>): number {
  const t = Date.parse(doc.documentDate ?? doc.createdOn ?? '');
  return Number.isNaN(t) ? 0 : t;
}

/** The newest `n` documents by the date the document was issued (else uploaded). */
export function mostRecentDocuments<T extends Pick<PatientDocument, 'documentDate' | 'createdOn'>>(docs: readonly T[], n = 5): T[] {
  return [...docs].sort((a, b) => when(b) - when(a)).slice(0, n);
}

/** "documents" count text: "5", or "20+" when the list is only the first page. */
export function countText(count: number, pageSize: number): string {
  return count >= pageSize ? `${pageSize}+` : String(count);
}

/** The summary line over the chart's tabs: "5 documents from 1 provider". Only true facts: with no documents, nothing. */
export function documentsSummary(docs: readonly Pick<PatientDocument, 'facilityName' | 'doctorName'>[], pageSize: number): string | null {
  if (docs.length === 0) return null;
  const providers = providerCounts(docs).length;
  const n = countText(docs.length, pageSize);
  const noun = docs.length === 1 ? 'document' : 'documents';
  // A full first page may hold more than it shows: the provider count would be a floor, so it is not stated.
  const from = docs.length >= pageSize || providers === 0 ? '' : ` from ${providers} ${providers === 1 ? 'provider' : 'providers'}`;
  return `${n} ${noun}${from}`;
}

export type DocumentKind = 'lab' | 'visit' | 'imaging' | 'prescription' | 'referral' | 'consent' | 'other';

/** The kind a document's type text names (the icon follows it). */
export function documentKind(type: string | null | undefined): DocumentKind {
  const t = (type ?? '').toLowerCase();
  if (/lab|pathology|blood/.test(t)) return 'lab';
  if (/radiol|imag|x-?ray|scan|mri|ultrasound/.test(t)) return 'imaging';
  if (/prescri|medicat/.test(t)) return 'prescription';
  if (/refer/.test(t)) return 'referral';
  if (/consent/.test(t)) return 'consent';
  if (/visit|note|encounter|consult|discharge|summary|clinic/.test(t)) return 'visit';
  return 'other';
}

/** "10 Aug 2026", or null for a missing / unreadable date. */
export function overviewDate(iso: string | null | undefined): string | null {
  const t = Date.parse(iso ?? '');
  if (Number.isNaN(t)) return null;
  return new Date(t).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
